import { formatNumber } from "utils/formatNumber";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import AsinProductCell from "views/merchandise/cells/AsinCell";

type AudienceTemplate = {
  audience_template_name: string;
  audience_byline: string;
  audience_description: string;
  recommended_lookalike_range: object;
  optional_fields: string[];
  required_fields: string[];
  recommended_tags: string[];
};

export const formatAdType = (v: string) => {
  let formattedType = toTitleCase(snakeCaseToSentenceCase(v));

  if (formattedType.toLowerCase() === "dsp") {
    formattedType = formattedType.toUpperCase();
  }

  if (formattedType[formattedType.length - 1] === "s") {
    formattedType = formattedType.slice(0, -1);
  }

  return formattedType;
};

export const mapCountryCodeToName = (code: string) => {
  try {
    if (!code) {
      return code;
    }
    const format = new Intl.DisplayNames(["en"], { type: "region" });
    return format.of(code);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return code;
  }
};

export const mapStateCodeToName = (code: string) => {
  switch (code) {
    case "US-AL":
      return "Alabama";
    case "US-AK":
      return "Alaska";
    case "US-AZ":
      return "Arizona";
    case "US-AR":
      return "Arkansas";
    case "US-CA":
      return "California";
    case "US-CO":
      return "Colorado";
    case "US-CT":
      return "Connecticut";
    case "US-DE":
      return "Delaware";
    case "US-FL":
      return "Florida";
    case "US-GA":
      return "Georgia";
    case "US-HI":
      return "Hawaii";
    case "US-ID":
      return "Idaho";
    case "US-IL":
      return "Illinois";
    case "US-IN":
      return "Indiana";
    case "US-IA":
      return "Iowa";
    case "US-KS":
      return "Kansas";
    case "US-KY":
      return "Kentucky";
    case "US-LA":
      return "Louisiana";
    case "US-ME":
      return "Maine";
    case "US-MD":
      return "Maryland";
    case "US-MA":
      return "Massachusetts";
    case "US-MI":
      return "Michigan";
    case "US-MN":
      return "Minnesota";
    case "US-MS":
      return "Mississippi";
    case "US-MO":
      return "Missouri";
    case "US-MT":
      return "Montana";
    case "US-NE":
      return "Nebraska";
    case "US-NV":
      return "Nevada";
    case "US-NH":
      return "New Hampshire";
    case "US-NJ":
      return "New Jersey";
    case "US-NM":
      return "New Mexico";
    case "US-NY":
      return "New York";
    case "US-NC":
      return "North Carolina";
    case "US-ND":
      return "North Dakota";
    case "US-OH":
      return "Ohio";
    case "US-OK":
      return "Oklahoma";
    case "US-OR":
      return "Oregon";
    case "US-PA":
      return "Pennsylvania";
    case "US-RI":
      return "Rhode Island";
    case "US-SC":
      return "South Carolina";
    case "US-SD":
      return "South Dakota";
    case "US-TN":
      return "Tennessee";
    case "US-TX":
      return "Texas";
    case "US-UT":
      return "Utah";
    case "US-VT":
      return "Vermont";
    case "US-VA":
      return "Virginia";
    case "US-WA":
      return "Washington";
    case "US-WV":
      return "West Virginia";
    case "US-WI":
      return "Wisconsin";
    case "US-WY":
      return "Wyoming";
    default:
      return code || "Unknown";
  }
};

export const getTemplateSpec = (templates: AudienceTemplate[], templateName: string) => {
  return templates.find(
    (audienceTemplate: AudienceTemplate) => audienceTemplate.audience_template_name === templateName,
  );
};

export const CANNED_QUERIES = [
  {
    name: "DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION",
    display_name: "DSP Performance By Location",
    byline: "Optimize your ad strategy with location-specific insights.",
    description:
      "The DSP Performance by Location report provides a detailed breakdown of your ad performance across different regions. Use this data to identify high-performing markets, optimize your ad spend, and tailor your campaigns to target specific audiences.",
    parameter_list: ["days"],
    href: "/user/amc/dsplocation",
  },
  {
    name: "CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION",
    byline: "Discover the campaigns that initiate customer journeys.",
    display_name: "Campaign Performance, First Touch Attribution",
    description:
      "The Campaign Performance, First Touch Attribution report evaluates your ad campaigns using first touch attribution, highlighting which efforts are driving initial traffic that leads to sales. This insight helps you understand the true origins of your customers’ journeys, allowing you to optimize your marketing strategy for better results.",
    parameter_list: ["days"],
  },
  {
    name: "EXPOSURE_SEGMENT_PERFORMANCE",
    byline: "Measure the impact of each ad type on consumer reach and purchase behavior.",
    display_name: "Performance by Exposure Segment",
    description:
      "The Performance by Exposure Segment report breaks down the key metrics for each ad type, providing insights into unique reach and purchase rates. Use this data to assess the effectiveness of your advertising efforts and optimize your campaigns for maximum impact.",
    parameter_list: ["days"],
  },
  {
    name: "CAMPAIGN_STR_PLACEMENT_PERFORMANCE",
    byline: "Search Term performance broken down Placement",
    display_name: "Search Term Placement",
    description:
      "The Search Term Placement Report provides a detailed breakdown of your search term performance by placement.",
    parameter_list: ["days", "tag"],
  },
  {
    name: "GATEWAY_ASINS_INTERNAL",
    byline: "Identify the products that attract new customers.",
    display_name: "Gateway ASINs",
    description:
      "The Gateway ASINs feature highlights the products that are most effective in driving new-to-brand sales. Use this insight to focus your marketing efforts on the items that bring in new customers, expanding your brand’s reach.",
    parameter_list: ["days"],
  },
  {
    name: "LTV_BY_AD_EXPOSURE_INTERNAL",
    byline: "Assess the impact of ad exposure on customer lifetime value.",
    display_name: "Lifetime Value by Exposure Segment",
    description:
      "The Lifetime Value by Exposure Segment report compares the lifetime value of consumers exposed to your ads with those who were not. This analysis helps you determine if your advertising strategy is effectively targeting high-value customers, ensuring your efforts are driving meaningful brand growth..",
    parameter_list: ["days"],
    required_datasources: ["conversions_all"],
  },
  {
    name: "SUBSCRIBE_AND_SAVE_PERFORMANCE",
    byline: "Track the products fueling your Subscribe and Save success.",
    display_name: "Subscribe and Save Performance",
    description:
      "The Subscribe and Save Performance report identifies the products that are driving recurring sales through the Subscribe and Save program. Use this data to optimize your product offerings and maximize long-term customer retention.",
    parameter_list: ["days"],
    required_datasources: ["conversions_all"],
  },
  {
    name: "REACH_IMPRESSION_FREQUENCY",
    display_name: "Reach and Impression Frequency",
    byline: "See how often shoppers are exposed to your ads",
    description:
      "Break down user-level ad impressions into frequency buckets—ranging from one-time views to 15+ impressions. Identify how many times shoppers typically see your ads before taking action and optimize for ideal exposure levels.",
    parameter_list: ["days", "tag"],
  },
  {
    name: "ASIN_PURCHASE_OVERLAP",
    display_name: "ASIN Purchase Overlap",
    byline: "Discover which products drive follow-up purchases",
    description:
      "Analyze how often a secondary product (overlap ASIN) is purchased after a customer buys a primary product (lead ASIN). Includes conversion percentage based on purchase sequences—perfect for spotting upsell and cross-sell opportunities.",
    parameter_list: ["days"],
  },
  {
    name: "TIME_TO_CONVERSION",
    display_name: "Time to Conversion",
    byline: "Measure how long it takes for ads to convert",
    description:
      "See how long it takes users to convert after seeing your ad, broken into time-based buckets. Includes both direct ASIN purchases and total brand conversions—helping you understand the delay between impression and action across your funnel.",
    parameter_list: ["days"],
  },
  {
    display_name: "New to Brand Sales",
    name: "NEW_TO_BRAND_CONVERSIONS",
    byline: "Compare sales performance between new and returning customers",
    description:
      "Track the number of sales, units, and conversions driven by new-to-brand customers versus returning ones. Understand how effectively your campaigns are attracting fresh shoppers and how their purchase behavior compares to loyal buyers.",
    parameter_list: ["days"],
  },
];

export const FEATURED_REPORTS = [
  {
    display_name: "Shopper Insights",
    name: "SHOPPER_INSIGHTS",
    byline: "Unlock key shopper metrics and product performance insights.",
    description:
      "The Shopper Insights report provides a comprehensive view of your customer journey, highlighting Lifetime Value, Customer Acquisition Cost, Repeat Orders, and New-to-Brand Sales. It also identifies Halo and Gateway products, helping you optimize your product strategy and drive growth.",
    parameter_list: ["days"],
    href: "/user/amc/shopperinsightsV2",
    featured: true,
  },
  {
    display_name: "Path to Purchase",
    name: "PATH_TO_PURCHASE_GROUPS_INTERNAL",
    byline: "Map your customers’ journey to optimize ad effectiveness.",
    description:
      "The Path to Purchase report reveals the routes consumers take when buying your products, covering key touchpoints like DSP, branded searches, and various sponsored ad clicks. Use this report to fine-tune your funnel strategy and discover the most effective ad combinations that drive conversions.",
    parameter_list: ["days"],
    href: "/user/amc/conversionchart/reports",
    featured: true,
  },
];

export const AMC_QUERY_EXPLORER_BREADCRUMB_SPEC = {
  name: "AMC Dashboard",
  href: "/user/amc/dashboard",
  subTitle: "Placement",
};

export const AMC_REPORT_METRIC_OPTIONS = [
  { key: "gateway_asin_rank", compareKey: null },
  { key: "all_users_with_purchase", compareKey: null },
  { key: "ntb_users", compareKey: null },
  { key: "ntb_percentage", compareKey: null },
  { key: "distinct_user_count", compareKey: null },
  { key: "customer_cnt", compareKey: null },
  { key: "units_sold", compareKey: null },
  {
    key: "sales",
    compareKey: null,
    options: {
      metric: {
        id: "sales",
        format: "currency",
        accessor: (d: { sales: string }) => d.sales,
        name: "Sales",
        options: { checked: true },
      },
    },
  },
  { key: "ltv", compareKey: null },
  { key: "distinct_user_count", compareKey: null },
  {
    key: "engagement_scope",
    compareKey: null,
    options: {
      formatter: (v: string) => {
        return toTitleCase(snakeCaseToSentenceCase(v));
      },
      width: 300,
    },
  },
  {
    key: "campaign",
    compareKey: null,
    options: {
      width: 500,
      formatter: (v: string) => {
        return v;
      },
    },
  },
  { key: "purchases", compareKey: null },
  {
    key: "ad_product_type",
    compareKey: null,
    options: {
      formatter: formatAdType,
    },
  },
  { key: "total_impressions", compareKey: null },
  { key: "users_that_purchased", compareKey: null },
  { key: "unique_reach", compareKey: null },
  { key: "total_clicks", compareKey: null },

  { key: "dsp_clicks", compareKey: null },
  { key: "conversions", compareKey: null },
  { key: "dsp_impressions", compareKey: null },
  { key: "total_spend", compareKey: null },
  { key: "dsp_total_purchases", compareKey: null },
  { key: "total_product_sales", compareKey: null },

  { key: "roas", compareKey: null },
  { key: "purchase_rate", compareKey: null },
  {
    key: "sns_purchase_frequency",
    compareKey: null,
    options: {
      formatter: (v: number) => (isNaN(v) ? "-" : formatNumber(v)),
    },
  },
  { key: "total_sns_count", compareKey: null },
  { key: "percent_total_sns", compareKey: null },
  { key: "dsp_total_units", compareKey: null },
  { key: "user_reach", compareKey: null },
  { key: "users_that_converted", compareKey: null },
  { key: "users_that_clicked", compareKey: null },
  { key: "unique_reach", compareKey: null },
  { key: "users_that_purchased", compareKey: null },
  { key: "total_impressions", compareKey: null },
  { key: "impressions", compareKey: null },
  { key: "impression_cost", compareKey: null },
  { key: "total_clicks", compareKey: null },
  { key: "clicks", compareKey: null },
  { key: "ad_sales", compareKey: null },
  { key: "ad_spend", compareKey: null },
  { key: "purchases", compareKey: null },
  { key: "total_product_sales", compareKey: null },
  { key: "dsp_total_add_to_cart", compareKey: null },
  { key: "dsp_total_detail_page_views", compareKey: null },
  { key: "acos", compareKey: null },
  { key: "roas", compareKey: null },
  { key: "ctr", compareKey: null },
  { key: "cpc", compareKey: null },
  { key: "conversion_rate", compareKey: null },
  { key: "advertiser", compareKey: null },
  { key: "frequency_bucket", compareKey: null },
  { key: "users_in_bucket", compareKey: null },
  { key: "impressions_in_bucket", compareKey: null },
  { key: "percent_of_total_users", compareKey: null },
  { key: "lead_asin", compareKey: null },
  { key: "lead_asin_purchases", compareKey: null },
  { key: "dist_lead_asin_purchased_user_count", compareKey: null },
  {
    key: "overlap_asin",
    compareKey: null,
    options: {
      className: "justify-content-start mx-5 px-5",
      cellContainerClasses: "justify-content-start",
      formatter: (v: string) => {
        return <AsinProductCell asin={v} />;
      },
    },
  },

  { key: "dist_lead_and_overlap_asin_purchased_user_count", compareKey: null },
  { key: "user_overlap", compareKey: null },
  { key: "campaign_id_string", compareKey: null },
  { key: "time_to_conversion", compareKey: null },
  { key: "purchases", compareKey: null },
  { key: "total_brand_purchases", compareKey: null },
  { key: "< 1 Min", compareKey: null },
  { key: "From 1min - 10min", compareKey: null },
  { key: "From 10min - 30min", compareKey: null },
  { key: "From 30min - 1h", compareKey: null },
  { key: "From 1h - 2h", compareKey: null },
  { key: "From 2h - 12h", compareKey: null },
  { key: "From 12h - 24h", compareKey: null },
  { key: "From 1 - 3 Days", compareKey: null },
  { key: "From 3 - 5 Days", compareKey: null },
  { key: "From 5 - 7 Days", compareKey: null },
  { key: "More than 7 Days", compareKey: null },
  { key: "advertiser", compareKey: null },
  { key: "campaign_id", compareKey: null },
  { key: "ntb_conversions", compareKey: null },
  { key: "non_ntb_conversions", compareKey: null },
  { key: "ntb_conversions_pct", compareKey: null },
  { key: "ntb_units", compareKey: null },
  { key: "non_ntb_units", compareKey: null },
  { key: "ntb_units_pct", compareKey: null },
  { key: "ntb_product_sales", compareKey: null },
  { key: "non_ntb_product_sales", compareKey: null },
  { key: "ntb_product_sales_pct", compareKey: null },
  {
    key: "iso_country_code",
    compareKey: null,
    options: {
      className: "text-start",
      width: 100,
      metric: {
        id: "iso_country_code",
        format: "country",
        accessor: (d: { iso_country_code: string }) => mapCountryCodeToName(d.iso_country_code),
        name: "Country",
        options: { checked: true },
      },
    },
  },
  {
    key: "iso_state_province_code",
    compareKey: null,
    options: {
      width: 100,
      className: "text-start",
      metric: {
        id: "iso_state_province_code",
        format: "state",
        accessor: (d: { iso_state_province_code: string }) => mapStateCodeToName(d.iso_state_province_code),
        name: "State",
        options: { checked: true },
      },
    },
  },
  {
    key: "state",
    compareKey: null,
    options: {
      width: 100,
      className: "text-start",
      metric: {
        id: "state",
        format: "state",
        accessor: (d: { state: string }) => d.state,
        name: "State",
        options: { checked: true },
      },
    },
  },
  {
    key: "country",
    compareKey: null,
    options: {
      width: 100,
      className: "text-start",
      metric: {
        id: "country",
        format: "country",
        accessor: (d: { country: string }) => d.country,
        name: "Country",
        options: { checked: true },
      },
    },
  },
  { key: "dma_code", compareKey: null },
  {
    key: "customer_search_term",
    compareKey: null,
    options: {
      className: "d-flex",
      metric: {
        id: "customer_search_term",
        format: "text",
        accessor: (d: { customer_search_term: string }) => d.customer_search_term,
        name: "Search Term",
        options: { checked: true },
      },
    },
  },
  {
    key: "placement_type",
    compareKey: null,
    options: {
      className: "d-flex",
      metric: {
        id: "placement_type",
        format: "text",
        accessor: (d: { placement_type: string }) => d.placement_type,
        name: "Placement",
        options: { checked: true },
      },
    },
  },
];
export const time_to_conversion_metrics = [
  "< 1 Min",
  "From 1min - 10min",
  "From 10min - 30min",
  "From 30min - 1h",
  "From 1h - 2h",
  "From 2h - 12h",
  "From 12h - 24h",
  "From 1 - 3 Days",
  "From 3 - 5 Days",
  "From 5 - 7 Days",
  "More than 7 Days",
] as const;

export type TimeToConversionMetrics = (typeof time_to_conversion_metrics)[number];

export const TABLE_SPECS_BY_REPORT_TYPE = {
  GATEWAY_ASINS: {
    metrics: ["gateway_asin_rank", "all_users_with_purchase", "ntb_users", "ntb_percentage", "distinct_user_count"],
    defaultSorted: [{ id: "gateway_asin_rank", desc: false }],
    titleColLabel: "Tracked ASIN",
    searchFilterTargets: ["tracked_asin"],
  },
  LTV_BY_AD_EXPOSURE: {
    metrics: ["ltv", "customer_cnt", "units_sold", "sales"],
    defaultSorted: [{ id: "customer_cnt", desc: false }],
    titleColLabel: "Exposure",
    searchFilterTargets: ["exposure"],
  },
  EXPOSURE_SEGMENT_PERFORMANCE: {
    metrics: [
      "users_that_purchased",
      "unique_reach",
      "total_impressions",
      "total_clicks",
      "total_spend",
      "dsp_total_purchases",
      "total_product_sales",
      "purchase_rate",
    ],
    defaultSorted: [{ id: "total_impressions", desc: false }],
    titleColLabel: "Exposure",
    searchFilterTargets: ["exposure_group"],
  },
  SUBSCRIBE_AND_SAVE_PERFORMANCE: {
    metrics: ["sns_purchase_frequency", "total_sns_count", "percent_total_sns"],
    defaultSorted: [{ id: "total_sns_count", desc: false }],
    titleColLabel: "Tracked ASIN",
    searchFilterTargets: ["tracked_asin"],
  },
  CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION: {
    metrics: [
      "ad_product_type",
      "dsp_clicks",
      "conversions",
      "dsp_impressions",
      "roas",
      "total_product_sales",
      "dsp_total_units",
      "user_reach",
      "users_that_converted",
    ],
    defaultSorted: [{ id: "purchases", desc: true }],
    titleColLabel: "Campaign",
    searchFilterTargets: ["campaign", "ad_product_type"],
  },
  DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION: {
    metrics: [
      "country",
      "state",
      "unique_reach",
      "users_that_clicked",
      "total_impressions",
      "total_spend",
      "dsp_total_detail_page_views",
      "dsp_total_add_to_cart",
      "purchases",
      "total_product_sales",
    ],
    defaultSorted: [{ id: "total_impressions", desc: true }],
    titleColLabel: "Campaign",
    searchFilterTargets: ["campaign", "country", "state", "dma_code"],
  },
  CAMPAIGN_STR_PLACEMENT_PERFORMANCE: {
    metrics: [
      "placement_type",
      "impressions",
      "clicks",
      "conversions",
      "ad_spend",
      "ad_sales",
      "acos",
      "roas",
      "ctr",
      "conversion_rate",
      "cpc",
    ],
    defaultSorted: [{ id: "impressions", desc: true }],
    titleColLabel: "Search Term",
    searchFilterTargets: ["customer_search_term", "placement_type"],
  },
  REACH_IMPRESSION_FREQUENCY: {
    metrics: ["users_in_bucket", "impressions_in_bucket", "percent_of_total_users"],
    defaultSorted: [{ id: "users_in_bucket", desc: true }],
    titleColLabel: "Impressions per User",
    searchFilterTargets: ["users_in_bucket", "impressions_in_bucket", "percent_of_total_users"],
  },
  ASIN_PURCHASE_OVERLAP: {
    metrics: [
      "lead_asin_purchases",
      "dist_lead_asin_purchased_user_count",
      "dist_lead_and_overlap_asin_purchased_user_count",
      "user_overlap",
      "overlap_asin",
    ],
    defaultSorted: [{ id: "user_overlap", desc: true }],
    titleColLabel: "Lead ASIN",
    searchFilterTargets: [
      "lead_asin",
      "overlap_asin",
      "lead_asin_purchases",
      "dist_lead_asin_purchased_user_count",
      "dist_lead_and_overlap_asin_purchased_user_count",
      "user_overlap",
    ],
  },
  TIME_TO_CONVERSION: {
    metrics: time_to_conversion_metrics,
    defaultSorted: [{ id: "campaign", desc: true }],
    titleColLabel: "Campaign",
    searchFilterTargets: ["campaign"],
  },
  NEW_TO_BRAND_CONVERSIONS: {
    metrics: [
      "ntb_conversions",
      "non_ntb_conversions",
      "ntb_conversions_pct",
      "ntb_units",
      "non_ntb_units",
      "ntb_units_pct",
      "ntb_product_sales",
      "non_ntb_product_sales",
      "ntb_product_sales_pct",
    ],
    defaultSorted: [{ id: "campaign", desc: true }],
    titleColLabel: "Campaign",
    searchFilterTargets: ["campaign"],
  },
};

export const FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT = ["PATH_TO_PURCHASE_GROUPS_INTERNAL"];

export const AUDIENCE_LOOKALIKE_OPTIONS = [
  { value: null, label: "Exact" },
  { value: "MOST_SIMILAR", label: "Most Similar (Europe and the Americas: 500K-1M, Asia: 200K-300K)" },
  { value: "SIMILAR", label: "Similar (Europe and the Americas: 1.5M-3M, Asia: 450K-900K)" },
  { value: "BALANCED", label: "Balanced (Europe and the Americas: 2M-5.5M, Asia: 600K-1.65M)" },
  { value: "BROAD", label: "Broad (Europe and the Americas: 2.5M-7.5M, Asia: 750K-2.25M)" },
  { value: "MOST_BROAD", label: "Most Broad (Europe and the Americas: 3M-10M, Asia: 900K-3M)" },
];

export const MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP = {
  ATVPDKIKX0DER: "USA",
  A2EUQ1WTGCTBG2: "CA",
  A1F83G8C2ARO7P: "UK",
  A39IBJ37TRP1C6: "AU",
  A1PA6795UKMFR9: "DE",
  A2VIGQ35RCS4UG: "AE",
  A1AM78C64UM0Y8: "MX",
  A1805IZSGTT6HS: "NL",
  A1RKKUPIHCS9HS: "ES",
  A21TJRUUN4KGV: "IN",
  A13V1IB3VIYZZH: "FR",
  APJ6JRA9NG5V4: "IT",
  AMEN7PMS3EDWL: "BE",
  A1VC38T7YXB528: "JP",
  A19VAU5U5O7RUS: "SG",
  A2NODRKZP88ZB9: "SE",
  A1C3SOZRARQ6R3: "PL",
  A33AVAJ2PDY3EV: "TR",
  A2Q3Y263D00KWC: "BR",
  A17E79C6D8DWNP: "SA",
  A28R8C7NBKEWEA: "IE",
};

export const COUNTRY_CODE_TO_MARKET_PLACE_ID_MAP = {
  USA: "ATVPDKIKX0DER",
  US: "ATVPDKIKX0DER",
  CA: "A2EUQ1WTGCTBG2",
  UK: "A1F83G8C2ARO7P",
  GB: "A1F83G8C2ARO7P",
  AU: "A39IBJ37TRP1C6",
  DE: "A1PA6795UKMFR9",
  AE: "A2VIGQ35RCS4UG",
  MX: "A1AM78C64UM0Y8",
  NL: "A1805IZSGTT6HS",
  ES: "A1RKKUPIHCS9HS",
  IN: "A21TJRUUN4KGV",
  FR: "A13V1IB3VIYZZH",
  IT: "APJ6JRA9NG5V4",
  BE: "AMEN7PMS3EDWL",
  JP: "A1VC38T7YXB528",
  SG: "A19VAU5U5O7RUS",
  SE: "A2NODRKZP88ZB9",
  PL: "A1C3SOZRARQ6R3",
  TR: "A33AVAJ2PDY3EV",
  BR: "A2Q3Y263D00KWC",
  SA: "A17E79C6D8DWNP",
  IE: "A28R8C7NBKEWEA",
};

export const AMC_AUDIENCE_STATUSES = [
  {
    label: "Running",
    value: "RUNNING",
  },
  {
    label: "Failed",
    value: "FAILED",
  },
  {
    label: "Succeeded",
    value: "SUCCEEDED",
  },
  {
    label: "Pending",
    value: "PENDING",
  },
  {
    label: "Deactivated",
    value: "DEACTIVATED",
  },
  {
    label: "Initial",
    value: "INITIAL",
  },
  {
    label: "Started",
    value: "STARTED",
  },
  {
    label: "Rejected",
    value: "REJECTED",
  },
];

export const DEFAULT_LOOKBACK_PERIOD_IN_DAYS = 30;

export const BASE_PATH_TO_PURCHASE_REPORT_CONFIG = {
  parameter_list: ["days"],
  parameters: { days: 90 },
  frequency: "daily",
  subscribed: true,
  isStatic: true,
  id: "pathToPurchaseBaseFeaturedReport",
};

export const AMC_CLIENT = 1;
